"use client";

import { useEffect, useRef } from "react";

interface SpotlightCursorProps {
  spotSize?: number;
}

export default function SpotlightCursor({ spotSize = 160 }: SpotlightCursorProps) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const stateRef = useRef({ x: 50, y: 50, visible: false });

  useEffect(() => {
    // Skip spotlight on touch/mobile devices (no pointer: fine)
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const el = elRef.current;
    if (!el) return;
    const parent = el.parentElement as HTMLElement | null;
    if (!parent) return;

    // dynamic activity tracking refs
    const activityRef = { value: 0 } as { value: number };
    const lastClient = { x: 0, y: 0 } as { x: number; y: number };
    let lastPointerTime: number | null = null;
    let lastRafTime: number | null = null;
    let smoothedSize = 0;

    // Activity is now defined as pointer movement occurrences.
    const SIZE_SCALE = 140; // px added at full activity
    const DECAY_PER_MS = 0.0012; // activity units decayed per ms (~1.2 per second)
    const SMOOTHING = 0.12; // smoothing for visual size changes

    // Use a viewport-based horizontal check instead of trying to detect text nodes.
    // If the pointer is within 20vw–80vw horizontally, lower the overlay opacity.

    const setInitialVars = () => {
      parent.style.setProperty("--cursor-x", `${stateRef.current.x}%`);
      parent.style.setProperty("--cursor-y", `${stateRef.current.y}%`);
      parent.style.setProperty("--spot-size", `0px`);
      parent.style.setProperty("--spot-overlay-opacity", "0.25");
    };

    const tick = (time: number) => {
      const now = time;
      const dt = Math.max(1, now - (lastRafTime ?? now));
      lastRafTime = now;

      // decay activity over time
      activityRef.value = Math.max(0, activityRef.value - DECAY_PER_MS * dt);

      // map activity to size
      const minSpot = Math.max(20, spotSize * 0.6);
      const maxSpot = Math.max(spotSize * 2.0, spotSize + 120);
      const target = stateRef.current.visible
        ? Math.min(maxSpot, Math.max(minSpot, spotSize + activityRef.value * SIZE_SCALE))
        : 0;

      // smooth size to avoid jitter
      smoothedSize = smoothedSize * (1 - SMOOTHING) + target * SMOOTHING;

      parent.style.setProperty("--cursor-x", `${stateRef.current.x}%`);
      parent.style.setProperty("--cursor-y", `${stateRef.current.y}%`);
      parent.style.setProperty("--spot-size", `${Math.round(smoothedSize)}px`);

      // continue loop while visible or activity remains
      if (stateRef.current.visible || activityRef.value > 0.001 || Math.abs(smoothedSize - target) > 0.5) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        lastRafTime = null;
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const rect = parent.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
      stateRef.current.x = x;
      stateRef.current.y = y;
      stateRef.current.visible = true;

      // lower overlay opacity when pointer is within 20vw..80vw of the viewport
      try {
        const vw = window.innerWidth;
        const leftBound = vw * 0.2;
        const rightBound = vw * 0.8;
        const within = e.clientX >= leftBound && e.clientX <= rightBound;
        const proportionalOpacityWithDistanceWithinCenterIsFullyTransparent = within
          ? 0.15 * (Math.abs(e.clientX - vw / 2) / (vw / 2))
          : 0.25;
        parent.style.setProperty("--spot-overlay-opacity", proportionalOpacityWithDistanceWithinCenterIsFullyTransparent.toString());
      } catch (err) {
        parent.style.setProperty("--spot-overlay-opacity", "0.25");
      }

      // Each pointermove counts as activity; increment a small amount per move.
      // This treats activity purely as "mouse move" occurrences rather than speed.
      activityRef.value = Math.min(1, activityRef.value + 0.18);

      lastPointerTime = performance.now();
      lastClient.x = e.clientX;
      lastClient.y = e.clientY;

      if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick);
    };

    const onPointerLeave = () => {
      stateRef.current.visible = false;
      // restore default overlay opacity when pointer leaves
      parent.style.setProperty("--spot-overlay-opacity", "0.25");
      // keep raf running so size can smoothly shrink to 0
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick);
    };

    parent.addEventListener("pointermove", onPointerMove, { passive: true });
    parent.addEventListener("pointerleave", onPointerLeave);
    parent.addEventListener("pointerdown", onPointerMove);
    parent.addEventListener("pointerup", onPointerLeave);

    // initialize CSS vars
    setInitialVars();

    return () => {
      parent.removeEventListener("pointermove", onPointerMove);
      parent.removeEventListener("pointerleave", onPointerLeave);
      parent.removeEventListener("pointerdown", onPointerMove);
      parent.removeEventListener("pointerup", onPointerLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [spotSize]);

  return <div ref={elRef} style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 999 }} />;
}
